import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import ReactPlayer from 'react-player/lazy';
import { GatsbyImage } from 'gatsby-plugin-image';
import { findDOMNode } from 'react-dom';
import screenfull from 'screenfull'
import renderIcon from '../../../../utils/renderIcon';
import { useGetMediaSize } from '../../../../utils/hooks';

const ns = `product-gallery-video`;

const getVideoUrl = _video => {
	let output;

	if (_video?.sources?.length > 0) {
		output = [];

		_video.sources.forEach(item => {
			output.push({
				src: item.url,
				type: item.mimeType,
			});
		});
	} else {
		output = _video.embeddedUrl;
	}
	return output;
};

const Video = ({
	video,
	activeIndex,
	shouldStop,
	setShouldStop,
	setPauseGallery,
	setMobilePlaying
}) => {
	const [isPlaying, setIsPlaying] = useState(false);

	const videoRef = useRef();

	const { isMobileAgent } = useGetMediaSize({ disableResize: true });

	const config = {
		youtube: {
			playerVars: {
				controls: 0,
				modestbranding: true,
				rel: 0,
			},
		},
		vimeo: {
			playerOptions: {
				controls: false,
				title: false,
			},
		},
	}

	/**
	 ** function handleMobilePlay()
	 * 
	 * Handles setting state for when a video should play. Uses @screenfull to trigger
	 * a fullscreen request. Does not trigger if screenfull or the player doesn't exist,
	 * or if the screen is already fullscreen.
	 */
	const handleMobilePlay = () => {
		// eslint-disable-next-line react/no-find-dom-node
		const player = findDOMNode(videoRef.current);

		setPauseGallery(true);
		setIsPlaying(true);
		setMobilePlaying(true);

		if (!player || !screenfull.isEnabled) return;

		screenfull.request(player);
	};

	/**
	 ** function handleVideoEnd()
	 * 
	 * Handles when the video ends or is paused. Uses @screenfull to trigger exiting fullscreen
	 * if it exists and the screen is currently fullscreen.
	 */
	const handleEnd = useCallback(() => {
		setPauseGallery(false);
		setIsPlaying(false);
		setMobilePlaying(false);

		if (!screenfull.isEnabled) return;

		screenfull.exit();
	}, [setMobilePlaying, setPauseGallery])

	const handlePlayPause = e => {
		e.preventDefault();

		if (isMobileAgent) {
			!isPlaying ? handleMobilePlay() : handleEnd();
		}
		else {
			setPauseGallery(!isPlaying);
			setIsPlaying(!isPlaying);
		}
	};

	const rootClassnames = classNames({
		[`${ns}`]: true,
		[`${ns}--playing`]: isPlaying,
	});

	/**
	 ** useEffect() handleScreenChange
	 *
	 * On initialization, this sets an event listener using screenfull that checks
	 * if the screen has entered or exited fullscreen mode. Sets the playing states
	 * to the current full screen mode. This will pause the video after escape click.
	 */
	useEffect(() => {
		if (!screenfull.isEnabled) return;

		const handleScreenChange = () => {
			if (screenfull.isFullscreen) return;

			handleEnd();
		}

		screenfull.on('change', handleScreenChange);

		// eslint-disable-next-line consistent-return
		return () => {
			screenfull.off('change', handleScreenChange);
		}
	}, [handleEnd, setMobilePlaying, setPauseGallery]);

	// activeIndex is the slide change event from the parent slider.
	// We want to stop playing the video on slide change
	useEffect(() => {
		setIsPlaying(false);
		setMobilePlaying(false);
	}, [activeIndex, setMobilePlaying]);

	// stop the video on swipe
	useEffect(() => {		
		if (isPlaying && shouldStop) {
			setShouldStop(false);
			setPauseGallery(false);
			setIsPlaying(false);
			setMobilePlaying(false);
			
			if (isMobileAgent && screenfull.isEnabled && screenfull.isFullscreen) {
				screenfull.exit();
			}
		}
	}, [isPlaying, shouldStop, setShouldStop, isMobileAgent, setPauseGallery, setMobilePlaying]);

	return (
		<div className={rootClassnames}>
			<button
				className={`${ns}__overlay`}
				onClick={handlePlayPause}
			>
				<GatsbyImage
					alt={video?.preview?.image?.altText}
					image={video?.preview?.image?.gatsbyImageData}
					loading={'eager'}
				/>
				<span aria-hidden className={`${ns}__play-icon`}>
					{renderIcon('video-play')}
				</span>
			</button>
			<div className={`${ns}__media`}>
				<ReactPlayer
					ref={videoRef}
					url={getVideoUrl(video)}
					playing={isPlaying}
					controls={false}
					width={'100%'}
					height={'100%'}
					config={config}
					onEnded={handleEnd}
				/>
			</div>
		</div>
	);
};

export default Video;
